/**
 * Ingot Custom Popup Enhanced JavaScript
 * Advanced popup functionality with analytics and enhanced UX
 */

// Ingot Popup Manager
window.IngotPopupManager = (function() {
  'use strict';

  // Ingot - Private variables
  let instances = [];
  let isAnyPopupOpen = false;

  // Ingot - Utility functions
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  function setCookie(name, value, days) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + days);
    document.cookie = `${name}=${value}; expires=${expiryDate.toUTCString()}; path=/`;
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Ingot - Analytics tracking
  function trackPopupEvent(eventName, popupId, customData = {}) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'Ingot_Popup',
        event_label: popupId,
        custom_popup_id: popupId,
        ...customData
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', `IngotPopup_${eventName}`, {
        popup_id: popupId,
        ...customData
      });
    }

    // Custom event for other tracking systems
    window.dispatchEvent(new CustomEvent('ingotPopupEvent', {
      detail: {
        event: eventName,
        popupId: popupId,
        timestamp: new Date().toISOString(),
        ...customData
      }
    }));
  }

  // Ingot - Enhanced Popup Class
  class IngotPopupEnhanced {
    constructor(element) {
      this.element = element;
      this.sectionId = element.dataset.sectionId;
      this.cookieName = `ingot-popup-${this.sectionId}`;
      this.delaySeconds = parseInt(element.dataset.delaySeconds) || 5;
      this.delayDays = parseInt(element.dataset.delayDays) || 30;
      this.testMode = element.dataset.testMode === 'true';
      this.isShown = false;
      this.showCount = parseInt(getCookie(`${this.cookieName}-count`)) || 0;
      
      // Ingot - Advanced settings
      this.maxShows = parseInt(element.dataset.maxShows) || 999;
      this.exitIntentEnabled = element.dataset.exitIntent === 'true';
      this.scrollPercentage = parseInt(element.dataset.scrollTrigger) || null;
      this.timeOnPage = parseInt(element.dataset.timeOnPage) || null;
      
      this.modal = new theme.Modals(`IngotCustomPopup-${this.sectionId}`, 'ingot-custom-popup');
      this.init();
    }

    // Ingot Custom Start - client-side date window 07/10/2026
    // Dates are YYYY-MM-DD, read as local midnight. The end date is
    // inclusive, so the popup stops at the start of the following day.
    inDateWindow() {
      const parse = (value) => {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
        return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
      };
      const now = new Date();
      const start = parse(this.element.dataset.startDate);
      const end = parse(this.element.dataset.endDate);

      if (start && now < start) return false;
      if (end) {
        end.setDate(end.getDate() + 1);
        if (now >= end) return false;
      }
      return true;
    }
    // Ingot Custom End

    init() {
      // Ingot - Check if should show
      if (!this.shouldShow()) {
        return;
      }

      this.bindEvents();
      this.setupTriggers();
      
      // Ingot - Track popup loaded
      trackPopupEvent('popup_loaded', this.sectionId, {
        show_count: this.showCount + 1
      });
    }

    shouldShow() {
      // Ingot Custom Start - client-side date window 07/10/2026
      // The Liquid date check alone isn't enough: Shopify caches rendered
      // pages, so 'now' can be frozen at cache time and a cached page keeps
      // the popup past its end date. Re-check against the visitor's clock.
      if (!this.inDateWindow()) {
        return false;
      }
      // Ingot Custom End

      // Ingot - Test mode always shows
      if (this.testMode) {
        return true;
      }

      // Ingot - Check cookie existence
      if (getCookie(this.cookieName)) {
        return false;
      }

      // Ingot - Check max shows
      if (this.showCount >= this.maxShows) {
        return false;
      }

      // Ingot - Don't show on certain pages
      const excludedPaths = ['/challenge', '/password', '/cart', '/checkout'];
      if (excludedPaths.some(path => window.location.pathname.includes(path))) {
        return false;
      }

      // Ingot - Don't show if another popup is open
      if (isAnyPopupOpen) {
        return false;
      }

      return true;
    }

    setupTriggers() {
      const triggers = [];

      // Ingot - Time delay trigger
      if (this.delaySeconds > 0) {
        triggers.push(this.setupTimeDelay());
      }

      // Ingot - Exit intent trigger
      if (this.exitIntentEnabled) {
        triggers.push(this.setupExitIntent());
      }

      // Ingot - Scroll percentage trigger
      if (this.scrollPercentage) {
        triggers.push(this.setupScrollTrigger());
      }

      // Ingot - Time on page trigger
      if (this.timeOnPage) {
        triggers.push(this.setupTimeOnPageTrigger());
      }

      return triggers;
    }

    setupTimeDelay() {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (!this.isShown) {
            resolve('time_delay');
          }
        }, this.delaySeconds * 1000);
      });
    }

    setupExitIntent() {
      return new Promise((resolve) => {
        const handleMouseLeave = debounce((e) => {
          if (e.clientY <= 0 && !this.isShown) {
            resolve('exit_intent');
            document.removeEventListener('mouseleave', handleMouseLeave);
          }
        }, 100);

        document.addEventListener('mouseleave', handleMouseLeave);
      });
    }

    setupScrollTrigger() {
      return new Promise((resolve) => {
        const handleScroll = debounce(() => {
          const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
          if (scrolled >= this.scrollPercentage && !this.isShown) {
            resolve('scroll_trigger');
            window.removeEventListener('scroll', handleScroll);
          }
        }, 100);

        window.addEventListener('scroll', handleScroll);
      });
    }

    setupTimeOnPageTrigger() {
      return new Promise((resolve) => {
        setTimeout(() => {
          if (!this.isShown) {
            resolve('time_on_page');
          }
        }, this.timeOnPage * 1000);
      });
    }

    bindEvents() {
      // Ingot - Close buttons
      const closeButtons = this.element.querySelectorAll('[data-ingot-popup-close]');
      closeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close('user_close');
        });
      });

      // Ingot - Track button clicks
      const actionButtons = this.element.querySelectorAll('.ingot-popup__primary-btn, .ingot-popup__secondary-btn');
      actionButtons.forEach((btn, index) => {
        btn.addEventListener('click', () => {
          const buttonType = btn.classList.contains('ingot-popup__primary-btn') ? 'primary' : 'secondary';
          trackPopupEvent('button_click', this.sectionId, {
            button_type: buttonType,
            button_index: index,
            button_text: btn.textContent.trim()
          });
        });
      });

      // Ingot - Modal events
      document.addEventListener(`modalOpen.IngotCustomPopup-${this.sectionId}`, () => {
        this.onShow();
      });

      document.addEventListener(`modalClose.IngotCustomPopup-${this.sectionId}`, () => {
        this.onClose();
      });

      // Ingot - Shopify theme editor support
      document.addEventListener('shopify:section:select', (evt) => {
        if (evt.detail.sectionId === this.sectionId) {
          this.show('editor_preview');
        }
      });

      document.addEventListener('shopify:section:deselect', (evt) => {
        if (evt.detail.sectionId === this.sectionId) {
          this.close('editor_close');
        }
      });
    }

    async show(trigger = 'unknown') {
      if (this.isShown || !this.modal) {
        return;
      }

      this.isShown = true;
      isAnyPopupOpen = true;
      
      // Ingot - Track show event
      trackPopupEvent('popup_shown', this.sectionId, {
        trigger: trigger,
        show_count: this.showCount + 1,
        time_on_page: Math.round((Date.now() - window.ingotPageLoadTime) / 1000)
      });

      this.modal.open();
    }

    close(reason = 'unknown') {
      if (!this.isShown || !this.modal) {
        return;
      }

      // Ingot - Track close event
      trackPopupEvent('popup_closed', this.sectionId, {
        close_reason: reason,
        time_shown: Math.round((Date.now() - this.showTime) / 1000)
      });

      this.modal.close();
    }

    onShow() {
      this.showTime = Date.now();
      this.element.classList.add('ingot-popup--active');
      
      // Ingot - Update show count
      this.showCount++;
      setCookie(`${this.cookieName}-count`, this.showCount, 365);
      
      // Ingot - Add keyboard listener
      this.keyboardHandler = (e) => {
        if (e.key === 'Escape') {
          this.close('keyboard_escape');
        }
      };
      document.addEventListener('keydown', this.keyboardHandler);
    }

    onClose() {
      this.isShown = false;
      isAnyPopupOpen = false;
      this.element.classList.remove('ingot-popup--active');
      
      // Ingot - Set cookie to prevent reshowing
      if (!this.testMode) {
        setCookie(this.cookieName, 'shown', this.delayDays);
      }
      
      // Ingot - Remove keyboard listener
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler);
      }
    }
  }

  // Ingot - Public API
  return {
    init: function() {
      // Ingot - Track page load time
      window.ingotPageLoadTime = Date.now();
      
      // Ingot - Initialize all popups
      const popupElements = document.querySelectorAll('[data-section-type="ingot-custom-popup"]');
      popupElements.forEach(element => {
        const popup = new IngotPopupEnhanced(element);
        instances.push(popup);
      });
    },

    showPopup: function(sectionId) {
      const popup = instances.find(p => p.sectionId === sectionId);
      if (popup) {
        popup.show('api_call');
      }
    },

    closePopup: function(sectionId) {
      const popup = instances.find(p => p.sectionId === sectionId);
      if (popup) {
        popup.close('api_call');
      }
    },

    closeAllPopups: function() {
      instances.forEach(popup => {
        if (popup.isShown) {
          popup.close('close_all');
        }
      });
    },

    getInstances: function() {
      return instances;
    }
  };
})();

// Ingot - Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  IngotPopupManager.init();
});

// Ingot - Export for external use
window.ingotPopup = {
  show: IngotPopupManager.showPopup,
  close: IngotPopupManager.closePopup,
  closeAll: IngotPopupManager.closeAllPopups
};